// 全体チャット。PCは右カラム常設（折り畳み可）、スマホは下タブから開くパネル。
// 受信は WebSocket（/api/chat/ws、受信専用）、送信は HTTP（POST /api/chat/messages）。
// 受信の形式：{t:'history', messages} / {t:'message', message} / {t:'delete', ids}。'ping' を送ると 'pong'。
// 切れたら間隔を伸ばしながら再接続。本文・名前は textContent で入れ、HTML として解釈させない。
import { api, ApiError, chatSocketUrl, type ChatMessage } from '../api';
import { refreshMe, state, setSetting, subscribe } from '../state';
import { sfx } from '../audio';
import { $, chatTime, esc } from './dom';
import { icon } from './icons';
import { avatarHtml, myAvatar, myFrame } from './avatar';
import { openModal, toast } from './overlay';
import { renderUnreadChat } from './shell';
import { designWidth, isMobileLayout, onViewportChange, sideChat } from './viewport';
import { buildId, recentErrors } from '../error-log';
import { guestBlocked } from './google-link';
import { refreshSupport, setSupportUnreadListener, supportUnread } from './support';
import { go } from '../router';
import { openContact } from './contact';
import { SUPPORT } from '@hyakki/core';

const MAX_LEN = 140;
let ws: WebSocket | null = null;
let status: 'off' | 'connecting' | 'open' | 'closed' = 'off';
let retry = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let messages: ChatMessage[] = [];
let shownOthersFeats: boolean | undefined;
let unread = 0;
let started = false;
let sending = false;
/** 「報告」：不具合・感想を運営だけに送る（チャットには流れない）。端末・画面・直近の例外を添える */
let reportMode = false;
/** ブロックした相手（サーバーに保存し、サーバーが配信しない。この端末でも隠す） */
let muted = new Map<string, { name: string | null }>();

/** 上部バー・下タブの印：全体チャットの未読＋運営からの返事 */
export const chatUnread = () => unread + supportUnread();
const app = () => document.getElementById('app')!;
// 横長の画面で帯の右に開いたまま並べているときは、下から出す札（スマホ）ではない
const mobile = () => isMobileLayout() && !sideChat();
// 狭いPCでは本文の幅を優先し、チャットは必要なときだけ重ねて開く。
// 広いPCの表示設定は保持し、ウィンドウを縮めても保存値を書き換えない。
const compactDesktop = { get matches() { return !isMobileLayout() && designWidth() <= 1399; } };
let compactOpen = false;
const desktopOpen = () => (compactDesktop.matches ? compactOpen : state.settings.chatOpen);
const syncChatLayout = () => app().classList.toggle('chat-collapsed', !desktopOpen());
const isVisible = () => sideChat() || (mobile() ? app().classList.contains('chat-mobile-open') : desktopOpen());

export function toggleChat(force?: boolean) {
  if (mobile()) {
    const open = force ?? !app().classList.contains('chat-mobile-open');
    app().classList.toggle('chat-mobile-open', open);
  } else {
    if (compactDesktop.matches) compactOpen = force ?? !compactOpen;
    else setSetting('chatOpen', force ?? !state.settings.chatOpen);
    syncChatLayout();
  }
  if (isVisible()) {
    unread = 0;
    renderUnreadChat();
    renderRailBadge();
    scrollBottom();
  }
}

const GUEST_NOTE = 'ゲストは発言できません。設定の「アカウント」からGoogleと連携すると発言できます。';

export function initChat() {
  const el = $('#chat')!;
  syncChatLayout();
  let wasCompact = compactDesktop.matches;
  onViewportChange(() => {
    // 帯の横に並べたら、スマホの札は閉じる
    if (sideChat()) app().classList.remove('chat-mobile-open');
    if (compactDesktop.matches === wasCompact) return;
    wasCompact = compactDesktop.matches;
    compactOpen = false;
    syncChatLayout();
  });
  el.innerHTML = `
    <header class="chat-head">
      <h2>全体チャット</h2>
      <button class="chat-support" type="button" data-chat="support"><img src="/art/support/okami-normal.webp" alt="">${esc(SUPPORT.name)}に相談<em class="badge" hidden></em></button>
      <span class="chat-status" aria-live="polite"></span>
      <button class="icon-btn chat-toggle" data-chat="toggle" aria-label="チャットを閉じる"><span class="minus" aria-hidden="true"></span></button>
    </header>
    <ol class="chat-list" aria-live="polite" aria-relevant="additions"></ol>
    <form class="chat-form" autocomplete="off">
      <button class="chat-report" type="button">報告・要望</button>
      <input class="chat-input" name="text" maxlength="${MAX_LEN}" placeholder="メッセージを入力…" aria-label="チャットの入力" enterkeyhint="send">
      <button class="btn primary chat-send" type="submit">送信</button>
    </form>
    <button class="chat-rail" data-chat="toggle" aria-label="チャットを開く">${icon('chat')}<span>全体チャット</span><em class="badge" hidden></em></button>`;
  const input = el.querySelector<HTMLInputElement>('.chat-input')!;
  // ゲスト（Google 未連携）は読むだけ。発言・通報には Google 連携が必要
  const reportBtn = el.querySelector<HTMLButtonElement>('.chat-report')!;
  const sendBtn = el.querySelector<HTMLButtonElement>('.chat-send')!;
  const syncGuest = () => {
    input.placeholder = reportMode ? '不具合・感想（端末の情報も送ります）' : state.me?.user.guest ? 'Googleと連携すると発言できます' : 'メッセージを入力…';
    reportBtn.setAttribute('aria-pressed', String(reportMode));
    sendBtn.textContent = reportMode ? '報告' : '送信';
    el.querySelector('.chat-form')!.classList.toggle('reporting', reportMode);
  };
  syncGuest();
  setReportMode = (on: boolean) => {
    reportMode = on;
    syncGuest();
  };
  // 「報告・要望」：メニューと同じお問い合わせの小窓（スクショ・送った内容も見られる）
  reportBtn.addEventListener('click', () => {
    if (mobile()) app().classList.remove('chat-mobile-open');
    openContact('bug');
  });
  subscribe((ch) => {
    if (ch.has('me')) syncGuest();
    // ほかの人の実績を出さない（設定の「チャット」）を切り替えたら並べ直す
    if (ch.has('settings') && state.settings.hideOthersFeats !== shownOthersFeats) {
      shownOthersFeats = state.settings.hideOthersFeats;
      render();
    }
    // 設定を変えた瞬間に、表示中の自分の発言にも反映する。下書き・スクロールは維持。
    if (ch.has('profile') || ch.has('me')) {
      el.querySelectorAll('.msg.mine .msg-av').forEach(av => av.innerHTML = avatarHtml(myAvatar(), 44, '', myFrame()));
    }
  });
  el.querySelector('form')!.addEventListener('submit', (e) => {
    e.preventDefault();
    void send(input);
  });
  // 入力中のキー操作は盤面へ伝えない（入力の下書きは画面を移っても残る）
  input.addEventListener('keydown', (e) => e.stopPropagation());
  el.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const b = t.closest<HTMLElement>('[data-chat]');
    if (b?.dataset.chat === 'toggle') toggleChat();
    else if (b?.dataset.chat === 'retry') connect(true);
    else if (b?.dataset.chat === 'support') openSupport();
    const m = t.closest<HTMLElement>('[data-msg]');
    if (m && !b) messageMenu(m.dataset.msg!);
  });
  el.addEventListener('keydown', (e) => {
    const m = (e.target as HTMLElement).closest<HTMLElement>('[data-msg]');
    if (m && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      messageMenu(m.dataset.msg!);
    }
  });
  setSupportUnreadListener(() => {
    renderUnreadChat();
    renderRailBadge();
  });
  render();
}

/** 「たまきに相談」（茶屋の専用画面へ。スマホのチャットの札は閉じる） */
export function openSupport() {
  if (guestBlocked(`${SUPPORT.name}と話すには、Google連携が必要です（AIの返事にチャットチケットを使うため）。`, 'support')) return;
  if (mobile()) app().classList.remove('chat-mobile-open');
  go('support');
}

export function startChat() {
  if (started) return;
  started = true;
  void api
    .chatHistory()
    .then((r) => {
      if (!messages.length) {
        messages = r.messages.slice(-200);
        render();
      }
    })
    .catch(() => {});
  void loadBlocks();
  void refreshSupport();
  connect();
  window.addEventListener('online', () => connect(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && status !== 'open') connect(true);
    if (document.visibilityState === 'visible') void refreshSupport();
  });
}

export function stopChat() {
  started = false;
  if (retryTimer) clearTimeout(retryTimer);
  if (pingTimer) clearInterval(pingTimer);
  const s = ws;
  ws = null;
  s?.close();
  status = 'off';
  messages = [];
  render();
}

async function loadBlocks() {
  try {
    const r = await api.blocks();
    muted = new Map(r.blocks.map((b) => [b.userId, { name: b.name }]));
    render();
  } catch {
    /* 未実装・失敗でも続行 */
  }
}

function connect(now = false) {
  if (!started || status === 'open' || status === 'connecting') return;
  if (retryTimer) clearTimeout(retryTimer);
  if (now) retry = 0;
  status = 'connecting';
  renderStatus();
  let sock: WebSocket;
  try {
    sock = new WebSocket(chatSocketUrl());
  } catch {
    return scheduleRetry();
  }
  ws = sock;
  sock.onopen = () => {
    status = 'open';
    retry = 0;
    renderStatus();
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = setInterval(() => {
      if (ws === sock && sock.readyState === WebSocket.OPEN) sock.send('ping');
    }, 25_000);
  };
  sock.onmessage = (ev) => {
    let d: Record<string, unknown>;
    try {
      d = JSON.parse(String(ev.data)) as Record<string, unknown>;
    } catch {
      return; // 'pong' など
    }
    const t = d.t ?? d.type;
    if (t === 'history' && Array.isArray(d.messages)) {
      messages = (d.messages as ChatMessage[]).slice(-200);
      render();
    } else if (t === 'message' && d.message) {
      add(d.message as ChatMessage);
      // 寄進が流れた：全員の効果（/api/me の buffs）を取り直して TOP の帯に出す
      if ((d.message as ChatMessage).kind === 'kishin') void refreshMe().catch(() => {});
    }
    else if (t === 'delete') {
      const ids = new Set(Array.isArray(d.ids) ? (d.ids as string[]) : typeof d.id === 'string' ? [d.id] : []);
      messages = messages.filter((m) => !ids.has(m.id));
      render();
    }
  };
  sock.onclose = () => {
    if (ws !== sock) return;
    ws = null;
    if (pingTimer) clearInterval(pingTimer);
    scheduleRetry();
  };
  sock.onerror = () => sock.close();
}

function scheduleRetry() {
  status = 'closed';
  renderStatus();
  if (!started) return;
  const ms = Math.min(30_000, 1000 * 2 ** retry++) + Math.random() * 500;
  retryTimer = setTimeout(() => connect(), ms);
}

let setReportMode = (_on: boolean) => {};

/** 報告に添える情報（運営だけが見る） */
function reportContext() {
  const nav = navigator as Navigator & { standalone?: boolean };
  return {
    screen: location.hash.slice(0, 200),
    ua: navigator.userAgent,
    viewport: `${innerWidth}x${innerHeight}`,
    dpr: devicePixelRatio,
    standalone: nav.standalone === true || matchMedia('(display-mode: standalone)').matches,
    online: navigator.onLine,
    build: buildId(),
    errors: recentErrors(),
  };
}

async function send(input: HTMLInputElement) {
  const text = input.value.trim();
  if (!reportMode && guestBlocked('チャットは、Google連携すると発言できます。', 'chat')) return;
  if (!text || sending) return;
  if (text.length > MAX_LEN) return toast(`${MAX_LEN}文字までです。`, 'error');
  sending = true;
  const asReport = reportMode;
  try {
    const r = await api.chatSend(text, crypto.randomUUID?.() ?? String(Date.now()), asReport ? reportContext() : undefined);
    input.value = '';
    if (r.message) add(r.message); // WebSocket が切れていても自分の発言は見える（id で重複を除く）
    if (asReport) {
      setReportMode(false);
      toast('報告を送りました。運営だけが読みます。ありがとうございます。', 'good', 3000);
    }
  } catch (e) {
    const code = e instanceof ApiError ? e.code : '';
    const map: Record<string, string> = {
      rate: '連続で送信できません。少し待ってください。',
      length: `${MAX_LEN}文字までです。`,
      text_length: `${MAX_LEN}文字までです。`,
      text_lines: '改行は4回までです。',
      text_chars: '使えない文字（見えない文字・制御文字など）が含まれています。',
      text_url: 'URLやサイトのアドレスは送れません。',
      text_ng: '使えない言葉が含まれています。',
      text_empty: 'メッセージを入力してください。',
      silenced: '現在、発言が制限されています。',
      guest: GUEST_NOTE,
      ng: '使えない言葉が含まれています。',
      empty: 'メッセージを入力してください。',
    };
    toast(map[code] ?? '送信できませんでした。', 'error');
  } finally {
    sending = false;
  }
}

function add(m: ChatMessage) {
  if (messages.some((x) => x.id === m.id)) return; // 重複
  messages.push(m);
  if (messages.length > 200) messages.shift();
  const mine = m.userId === state.me?.user.id;
  const hidden = isHidden(m);
  if (!isVisible() && !mine && !hidden) {
    unread++;
    renderUnreadChat();
    renderRailBadge();
  }
  if (!mine && !hidden) sfx('chat', 0.4);
  appendOne(m);
}

const listEl = () => document.querySelector<HTMLOListElement>('#chat .chat-list');
function scrollBottom() {
  const l = listEl();
  if (l) l.scrollTop = l.scrollHeight;
}
/** 実績のお知らせ（式神が仲間に・国の制圧）。サーバーが name に「式神」「国盗り」を入れて流す */
const FEAT_NAMES = new Set(['式神', '国盗り']);
const isOthersFeat = (m: ChatMessage) => m.userId == null && FEAT_NAMES.has(m.name) && !m.text.startsWith(`${state.me?.user.displayName ?? ''}さんが`);
const isHidden = (m: ChatMessage) => (m.userId != null && muted.has(m.userId)) || (!!state.settings.hideOthersFeats && isOthersFeat(m));
const isSystem = (m: ChatMessage) => m.userId == null || m.kind === 'system' || m.kind === 'kishin';
/** お祝い（制圧・式神の加入・寄進）：クラッカーの印で、名前は出さない。運営のお知らせはベルと「運営」 */
const isCelebration = (m: ChatMessage) => isSystem(m) && m.name !== '運営' && m.name !== 'お知らせ' && !!m.name;

function itemHtml(m: ChatMessage) {
  if (isHidden(m)) return '';
  const mine = m.userId === state.me?.user.id;
  const sys = isSystem(m);

  const party = isCelebration(m);
  return `<li class="msg ${sys ? 'system' : ''} ${party ? 'celebrate' : ''} ${m.kind === 'kishin' ? 'kishin' : ''} ${mine ? 'mine' : ''}" data-msg="${esc(m.id)}" tabindex="0">
    ${sys ? `<span class="msg-av sys" aria-hidden="true">${icon(party ? 'cracker' : 'bell')}</span>` : `<span class="msg-av" aria-hidden="true">${avatarHtml(mine ? myAvatar() : m.avatar, 44, '', mine ? myFrame() : m.frame)}</span>`}
    <div class="msg-body"><div class="msg-head"><b class="msg-name"></b><time datetime="${new Date(m.at).toISOString()}">${chatTime(m.at)}</time></div><p class="msg-text"></p></div></li>`;
}
function fill(li: Element, m: ChatMessage) {
  li.querySelector('.msg-name')!.textContent = isCelebration(m) ? '' : m.userId == null ? '運営' : m.name;
  li.querySelector('.msg-text')!.textContent = m.text;
}

function render() {
  const l = listEl();
  if (!l) return;
  const visible = messages.filter((m) => !isHidden(m));
  if (!visible.length) {
    l.innerHTML = `<li class="chat-empty">${status === 'open' ? 'まだ発言はありません。' : status === 'off' ? 'ログインするとチャットに参加できます。' : 'チャットに接続しています…'}</li>`;
  } else {
    l.innerHTML = visible.map(itemHtml).join('');
    l.querySelectorAll('.msg').forEach((li, i) => fill(li, visible[i]));
  }
  renderStatus();
  scrollBottom();
}
function appendOne(m: ChatMessage) {
  const l = listEl();
  if (!l) return;
  const html = itemHtml(m);
  if (!html) return;
  l.querySelector('.chat-empty')?.remove();
  const near = l.scrollHeight - l.scrollTop - l.clientHeight < 80;
  l.insertAdjacentHTML('beforeend', html);
  fill(l.lastElementChild!, m);
  while (l.children.length > 200) l.firstElementChild?.remove();
  if (near || m.userId === state.me?.user.id) scrollBottom();
}

function renderStatus() {
  const s = document.querySelector<HTMLElement>('#chat .chat-status');
  if (!s) return;
  if (status === 'open') s.innerHTML = '<i class="dot ok"></i>接続中';
  else if (status === 'connecting') s.innerHTML = '<i class="dot wait"></i>接続中…';
  else if (status === 'closed') s.innerHTML = '<i class="dot ng"></i>切断 <button class="link" data-chat="retry">再接続</button>';
  else s.textContent = '';
  const btn = document.querySelector<HTMLButtonElement>('#chat .chat-send');
  if (btn) btn.disabled = status === 'off';
}
function renderRailBadge() {
  const b = document.querySelector<HTMLElement>('#chat .chat-rail .badge');
  if (b) {
    b.hidden = chatUnread() === 0;
    b.textContent = String(chatUnread());
  }
  const s = document.querySelector<HTMLElement>('#chat .chat-support .badge');
  if (s) {
    s.hidden = supportUnread() === 0;
    s.textContent = String(supportUnread());
  }
}

function messageMenu(id: string) {
  const m = messages.find((x) => x.id === id);
  if (!m || m.userId == null || m.userId === state.me?.user.id) return;
  const uid = m.userId;
  const md = openModal(
    `<p class="quote"></p>
     <div class="btn-col">
       <button class="btn" data-block>ブロック（この人の発言を表示しない）</button>
       <button class="btn danger" data-report>通報する</button>
     </div>`,
    { title: m.name },
  );
  md.body.querySelector('.quote')!.textContent = m.text;
  md.body.addEventListener('click', async (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-block]')) {
      try {
        await api.block(uid);
      } catch {
        /* サーバーに保存できなくても、この端末では隠す */
      }
      muted.set(uid, { name: m.name });
      md.close();
      render();
      toast('ブロックしました（設定から解除できます）');
    } else if (t.closest('[data-report]')) {
      md.close();
      if (state.me?.user.guest) return toast('通報するには、設定からGoogleと連携してください。', 'error', 4000);
      reportDialog(m);
    }
  });
}

function reportDialog(m: ChatMessage) {
  const md = openModal(
    `<form class="report-form">
      <p>通報の理由を選んでください。運営が確認します。</p>
      ${['迷惑行為・荒らし', '誹謗中傷', '個人情報', '不適切な内容', 'その他'].map((r, i) => `<label class="radio"><input type="radio" name="r" value="${r}" ${i ? '' : 'checked'}> ${r}</label>`).join('')}
      <div class="btn-row"><button class="btn" type="button" data-close>やめる</button><button class="btn danger" type="submit">通報する</button></div>
    </form>`,
    { title: '通報' },
  );
  md.body.querySelector('form')!.addEventListener('submit', async (e) => {
    e.preventDefault();
    const reason = new FormData(e.target as HTMLFormElement).get('r') as string;
    try {
      await api.report(m.id, reason);
      toast('通報しました。ご協力ありがとうございます。', 'good');
      md.close();
    } catch {
      toast('通報を送れませんでした。時間をおいてお試しください。', 'error');
    }
  });
}

export const mutedList = () => [...muted.entries()].map(([userId, v]) => ({ userId, ...v }));
export async function unmute(userId: string) {
  try {
    await api.unblock(userId);
  } catch {
    /* 無視 */
  }
  muted.delete(userId);
  render();
}
